<template>
  <div class="notifications-page">
    <section class="notifications-shell">
      <div class="notifications-hero">
        <div class="hero-copy">
          <nav class="notifications-breadcrumb" aria-label="Breadcrumb">
            <span>Home</span>
            <span class="breadcrumb-separator">/</span>
            <span class="breadcrumb-current">Notifications</span>
          </nav>

          <p class="hero-kicker">Inbox</p>
          <h1>Review platform notifications and recent follow-ups from one focused admin stream</h1>
          <p class="hero-description">
            Keep an eye on unread notices, global announcements, and the latest messages that may
            need your attention.
          </p>

          <div class="hero-metrics">
            <article class="metric-chip">
              <span class="metric-chip__label">Alerts</span>
              <strong class="metric-chip__value">{{ filteredNotifications.length }}</strong>
              <span class="metric-chip__meta">Matching results</span>
            </article>
            <article class="metric-chip">
              <span class="metric-chip__label">Unread</span>
              <strong class="metric-chip__value">{{ unreadCount }}</strong>
              <span class="metric-chip__meta">Pending review</span>
            </article>
          </div>
        </div>
      </div>

      <section class="toolbar">
        <div class="toolbar-copy">
          <p class="results-kicker">Message Center</p>
          <h2>Admin notifications</h2>
        </div>

        <div class="toolbar-actions">
          <button type="button" class="create-button" @click="openCreateModal">
            <i class="fas fa-plus" aria-hidden="true"></i>
            <span>Create notification</span>
          </button>
          <label class="search-shell" for="notification-search">
            <i class="fas fa-search" aria-hidden="true"></i>
            <input
              id="notification-search"
              v-model.trim="search"
              type="search"
              placeholder="Search titles or messages"
            />
          </label>
        </div>
      </section>

      <div
        v-if="showCreateModal"
        class="notification-modal-backdrop"
        @click.self="closeCreateModal"
      >
        <div class="notification-modal">
          <div class="notification-modal__hero">
            <div>
              <span class="notification-modal__eyebrow">Broadcast</span>
              <h3>
                {{
                  isGlobalDraft ? 'Create a public notification' : 'Create a direct notification'
                }}
              </h3>
              <p>
                {{
                  isGlobalDraft
                    ? 'Send a platform-wide update that every user can see in their notifications feed.'
                    : 'Choose a specific recipient and send a direct notification to that user.'
                }}
              </p>
            </div>
            <button
              type="button"
              class="notification-modal__close"
              aria-label="Close modal"
              @click="closeCreateModal"
            >
              <i class="fas fa-times" aria-hidden="true"></i>
            </button>
          </div>

          <form class="notification-form" @submit.prevent="submitNotification">
            <label class="notification-field" for="notification-delivery">
              <span>Delivery</span>
              <select id="notification-delivery" v-model="createForm.deliveryType">
                <option value="public">Public notification</option>
                <option value="direct">Direct notification</option>
              </select>
            </label>

            <label
              v-if="createForm.deliveryType === 'direct'"
              class="notification-field"
              for="notification-recipient"
            >
              <span>Recipient</span>
              <select
                id="notification-recipient"
                v-model="createForm.recipient"
                required
                :disabled="recipientsLoading"
              >
                <option disabled value="">Select a user</option>
                <option v-for="person in recipientOptions" :key="person.id" :value="person.id">
                  {{ person.label }}
                </option>
              </select>
            </label>

            <label class="notification-field" for="notification-title">
              <span>Title</span>
              <input
                id="notification-title"
                v-model.trim="createForm.title"
                type="text"
                maxlength="120"
                required
                placeholder="Enter a short notification title"
              />
            </label>

            <label class="notification-field" for="notification-message">
              <span>Message</span>
              <textarea
                id="notification-message"
                v-model.trim="createForm.message"
                rows="5"
                maxlength="500"
                required
                placeholder="Write the public update you want users to receive"
              ></textarea>
            </label>

            <div class="notification-modal__meta">
              <div class="notification-meta-pill">
                <span>Audience</span>
                <strong>{{ isGlobalDraft ? 'Public' : 'Direct user' }}</strong>
              </div>
              <div class="notification-meta-pill">
                <span>Delivery</span>
                <strong>{{ isGlobalDraft ? 'Global' : 'Targeted' }}</strong>
              </div>
            </div>

            <div class="notification-modal__actions">
              <button
                type="button"
                class="modal-button modal-button--ghost"
                @click="closeCreateModal"
              >
                Cancel
              </button>
              <button type="submit" class="modal-button modal-button--primary" :disabled="creating">
                <b-spinner v-if="creating" small />
                <span>{{ creating ? 'Publishing...' : 'Publish notification' }}</span>
              </button>
            </div>
          </form>
        </div>
      </div>

      <div v-if="loading" class="loading-state">
        <b-spinner />
        <p>Loading notifications...</p>
      </div>

      <template v-else>
        <div v-if="paginatedNotifications.length" class="notification-list">
          <article
            v-for="(item, index) in paginatedNotifications"
            :key="item.id || index"
            :class="['notification-card', { 'notification-card--unread': item.is_read === false }]"
          >
            <div class="card-glow"></div>

            <div class="notification-card__top">
              <div>
                <span class="notification-index">
                  Alert {{ padIndex((currentPage - 1) * perPage + index) }}
                </span>
                <h3>{{ item.title || 'Notification' }}</h3>
              </div>
              <span class="timestamp-pill">{{ item.is_global ? 'Global' : 'Direct' }}</span>
            </div>

            <p class="notification-message">
              {{ item.message || 'No notification message was provided.' }}
            </p>

            <div class="notification-meta">
              <div class="meta-item">
                <span class="meta-label">Status</span>
                <strong>{{ item.is_read === false ? 'Unread' : 'Read' }}</strong>
              </div>
              <div class="meta-item">
                <span class="meta-label">Audience</span>
                <strong>{{ item.is_global ? 'All users' : recipientLabel(item.recipient) }}</strong>
              </div>
            </div>

            <div class="notification-actions">
              <button
                v-if="item.is_read === false && item.id"
                type="button"
                class="action-button"
                @click="markNotificationAsRead(item.id)"
              >
                Mark as read
              </button>
              <span v-else class="read-pill">Already reviewed</span>
            </div>
          </article>
        </div>

        <div v-else class="empty-state">
          <h3>No notifications found</h3>
          <p>There are no notifications matching the current search right now.</p>
        </div>

        <div v-if="filteredNotifications.length" class="pagination-shell">
          <div class="pagination-copy">
            <label class="page-size-shell" for="notification-page-size">
              <span>Rows</span>
              <select id="notification-page-size" v-model="perPage">
                <option value="5">5</option>
                <option value="10">10</option>
                <option value="20">20</option>
              </select>
            </label>
            <p>Page {{ currentPage }} of {{ totalPages }}</p>
            <span
              >{{ filteredNotifications.length }} notification{{
                filteredNotifications.length === 1 ? '' : 's'
              }}</span
            >
          </div>

          <b-pagination
            v-model="currentPage"
            :total-rows="filteredNotifications.length"
            :per-page="perPage"
            pills
            align="right"
            aria-controls="notification-list"
          />
        </div>
      </template>
    </section>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import AdminServices from '../AdminService';
import {
  createNotification,
  getNotifications,
  markNotificationAsRead,
} from '../config/api-modules/api.notifications';

export default {
  name: 'AdminNotificationsPage',

  data() {
    return {
      loading: true,
      creating: false,
      recipientsLoading: false,
      showCreateModal: false,
      search: '',
      currentPage: 1,
      perPage: 10,
      notifications: [],
      recipientOptions: [],
      createForm: {
        deliveryType: 'public',
        recipient: '',
        title: '',
        message: '',
      },
    };
  },

  computed: {
    ...mapGetters('auth', ['user']),

    isAdminViewer() {
      return [1, 2].includes(Number(this.user?.type));
    },

    unreadCount() {
      return this.notifications.filter((item) => item.is_read === false).length;
    },

    filteredNotifications() {
      const searchTerm = this.search.toLowerCase();

      return this.notifications.filter((item) => {
        const haystack = [item.title, item.message].filter(Boolean).join(' ').toLowerCase();

        return haystack.includes(searchTerm);
      });
    },

    paginatedNotifications() {
      return this.filteredNotifications.slice(
        (this.currentPage - 1) * this.perPage,
        this.currentPage * this.perPage
      );
    },

    totalPages() {
      return Math.max(1, Math.ceil(this.filteredNotifications.length / this.perPage));
    },

    isGlobalDraft() {
      return this.createForm.deliveryType === 'public';
    },
  },

  watch: {
    search() {
      this.currentPage = 1;
    },

    perPage() {
      this.currentPage = 1;
    },

    'createForm.deliveryType': function (value) {
      if (value === 'public') {
        this.createForm.recipient = '';
      }
    },
  },

  methods: {
    async openCreateModal() {
      this.showCreateModal = true;
      if (!this.recipientOptions.length) {
        await this.fetchRecipients();
      }
    },

    closeCreateModal(force = false) {
      if (this.creating && !force) {
        return;
      }

      this.showCreateModal = false;
      this.createForm = {
        deliveryType: 'public',
        recipient: '',
        title: '',
        message: '',
      };
    },

    padIndex(index) {
      return String(index + 1).padStart(2, '0');
    },

    recipientLabel(recipient) {
      if (recipient === this.user?.id) {
        return 'You';
      }

      if (recipient === null || recipient === undefined) {
        return 'Unassigned';
      }

      return `User #${recipient}`;
    },

    normalizeResponse(response) {
      const records = response?.data?.results || response?.data || [];
      return Array.isArray(records) ? records : [];
    },

    normalizeRecipients(response) {
      const records = response?.data?.results || response?.data || [];
      if (!Array.isArray(records)) {
        return [];
      }

      return records.map((item) => {
        const username = item?.user?.username || item?.username || 'Unknown user';
        const email = item?.user?.email || item?.email || 'No email';

        return {
          id: item?.user?.id || item?.id,
          label: `${username} (${email})`,
        };
      });
    },

    async fetchRecipients() {
      this.recipientsLoading = true;

      try {
        const response = await AdminServices.getAdmin();
        this.recipientOptions = this.normalizeRecipients(response).filter((item) => item.id);
      } catch (error) {
        this.recipientOptions = [];
        this.$toast.show({
          type: 'ERROR',
          message: 'Unable to load users for direct notifications',
        });
      } finally {
        this.recipientsLoading = false;
      }
    },

    async fetchNotifications() {
      this.loading = true;

      try {
        const response = await getNotifications(this.isAdminViewer ? null : this.user?.id);
        this.notifications = this.normalizeResponse(response);
      } catch (error) {
        this.notifications = [];
        this.$toast.show({
          type: 'ERROR',
          message: 'Unable to load notifications',
        });
      } finally {
        this.loading = false;
      }
    },

    async submitNotification() {
      if (this.createForm.deliveryType === 'direct' && !this.createForm.recipient) {
        this.$toast.show({
          type: 'ERROR',
          message: 'Please select a user for the direct notification',
        });
        return;
      }

      this.creating = true;

      const payload = {
        recipient:
          this.createForm.deliveryType === 'public' ? 0 : Number(this.createForm.recipient),
        title: this.createForm.title,
        message: this.createForm.message,
        is_global: this.createForm.deliveryType === 'public',
        is_read: false,
      };

      try {
        const response = await createNotification(payload);
        const createdNotification = response?.data || payload;

        this.notifications = [createdNotification, ...this.notifications];
        this.closeCreateModal(true);
        this.$toast.show({
          type: 'SUCCESS',
          message: 'Public notification created',
        });
      } catch (error) {
        this.$toast.show({
          type: 'ERROR',
          message: 'Unable to create notification',
        });
      } finally {
        this.creating = false;
      }
    },

    async markNotificationAsRead(id) {
      try {
        await markNotificationAsRead(id);
        this.notifications = this.notifications.map((item) =>
          item.id === id ? { ...item, is_read: true } : item
        );
        this.$toast.show({
          type: 'SUCCESS',
          message: 'Notification marked as read',
        });
      } catch (error) {
        this.$toast.show({
          type: 'ERROR',
          message: 'Unable to mark notification as read',
        });
      }
    },
  },

  mounted() {
    this.fetchNotifications();
  },
};
</script>

<style lang="scss" scoped>
.notifications-page {
  min-height: 100vh;
  padding: 1.5rem 1rem 2rem;
  background: radial-gradient(circle at top left, rgba(209, 231, 255, 0.95), transparent 30%),
    radial-gradient(circle at top right, rgba(24, 98, 153, 0.14), transparent 24%),
    linear-gradient(180deg, #f4f8fc 0%, #f6fbff 44%, #eef4fb 100%);
}

.notifications-shell {
  max-width: 1320px;
  margin: 0 auto;
}

.notifications-hero {
  display: block;
  padding: 1.75rem;
  border: 1px solid rgba(20, 70, 110, 0.08);
  border-radius: 28px;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.97), rgba(243, 249, 255, 0.92)), #fff;
  box-shadow: 0 24px 60px rgba(29, 61, 94, 0.1);
}

.notifications-breadcrumb {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
  color: #6f7d8f;
  font-size: 0.9rem;
}

.breadcrumb-separator {
  opacity: 0.55;
}

.breadcrumb-current {
  color: #204d74;
  font-weight: 600;
}

.hero-kicker,
.results-kicker {
  margin-bottom: 0.65rem;
  color: #1f6da8;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.hero-copy h1,
.toolbar-copy h2 {
  margin: 0;
  color: #153754;
  line-height: 1.08;
}

.hero-copy h1 {
  font-size: clamp(2rem, 4vw, 3.35rem);
  width: 100%;
  max-width: none;
}

.hero-description {
  max-width: 60ch;
  margin: 1rem 0 0;
  color: #5f7488;
  font-size: 1rem;
  line-height: 1.7;
}

.hero-metrics {
  display: flex;
  flex-wrap: wrap;
  gap: 0.85rem;
  margin-top: 1.35rem;
}

.metric-chip {
  display: grid;
  grid-template-columns: auto auto;
  align-items: center;
  gap: 0.1rem 0.9rem;
  min-width: 180px;
  padding: 0.9rem 1rem;
  border-radius: 20px;
  background: rgba(244, 249, 255, 0.9);
  border: 1px solid rgba(20, 70, 110, 0.1);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.7);
}

.metric-chip__label {
  grid-column: 1;
  grid-row: 1;
  color: #6a8196;
  font-size: 0.74rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.metric-chip__value {
  grid-column: 2;
  grid-row: 1 / span 2;
  justify-self: end;
  color: #153754;
  font-size: 2rem;
  line-height: 1;
}

.metric-chip__meta {
  grid-column: 1;
  grid-row: 2;
  color: #62798e;
  font-size: 0.85rem;
  line-height: 1.35;
}

.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 1.5rem;
  padding: 1.2rem 1.3rem;
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.8);
  border: 1px solid rgba(20, 70, 110, 0.08);
  box-shadow: 0 16px 35px rgba(29, 61, 94, 0.06);
}

.toolbar-copy h2 {
  font-size: clamp(1.3rem, 2.5vw, 2rem);
}

.toolbar-actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.create-button {
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  border: none;
  border-radius: 999px;
  padding: 0.85rem 1rem;
  background: linear-gradient(135deg, #1f6da8, #3d95cf);
  color: #ffffff;
  font-weight: 700;
  box-shadow: 0 12px 26px rgba(31, 109, 168, 0.18);
  width: fit-content;
}

.notification-modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1200;
  display: grid;
  place-items: center;
  padding: 1rem;
  background: rgba(11, 28, 43, 0.34);
  backdrop-filter: blur(6px);
}

.notification-modal {
  width: min(100%, 640px);
  border-radius: 28px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.98), rgba(245, 250, 255, 0.96));
  border: 1px solid rgba(20, 70, 110, 0.08);
  box-shadow: 0 24px 60px rgba(18, 45, 69, 0.22);
  overflow: hidden;
}

.notification-modal__hero {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.4rem 1.4rem 1rem;
  border-bottom: 1px solid rgba(20, 70, 110, 0.08);
}

.notification-modal__eyebrow {
  display: inline-block;
  margin-bottom: 0.55rem;
  color: #1f6da8;
  font-size: 0.76rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.notification-modal__hero h3 {
  margin: 0;
  color: #153754;
  font-size: 1.6rem;
}

.notification-modal__hero p {
  margin: 0.7rem 0 0;
  color: #5f7488;
  line-height: 1.6;
}

.notification-modal__close {
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 999px;
  background: rgba(20, 70, 110, 0.08);
  color: #37556f;
}

.notification-form {
  display: grid;
  gap: 1rem;
  padding: 1.35rem 1.4rem 1.4rem;
}

.notification-field {
  display: grid;
  gap: 0.5rem;
}

.notification-field span {
  color: #33536e;
  font-size: 0.9rem;
  font-weight: 700;
}

.notification-field input,
.notification-field select,
.notification-field textarea {
  width: 100%;
  border: 1px solid rgba(20, 70, 110, 0.14);
  border-radius: 18px;
  padding: 0.95rem 1rem;
  background: #f8fbff;
  color: #153754;
  outline: none;
}

.notification-field textarea {
  resize: vertical;
  min-height: 140px;
}

.notification-modal__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.notification-meta-pill {
  min-width: 140px;
  padding: 0.85rem 0.95rem;
  border-radius: 18px;
  background: rgba(244, 249, 255, 0.95);
  border: 1px solid rgba(20, 70, 110, 0.08);
}

.notification-meta-pill span {
  display: block;
  color: #6a8196;
  font-size: 0.74rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.notification-meta-pill strong {
  display: block;
  margin-top: 0.3rem;
  color: #153754;
}

.notification-modal__actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
}

.modal-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.55rem;
  border: none;
  border-radius: 999px;
  padding: 0.82rem 1.15rem;
  font-weight: 700;
  width: fit-content;
}

.modal-button--ghost {
  background: rgba(20, 70, 110, 0.08);
  color: #35546d;
}

.modal-button--primary {
  background: linear-gradient(135deg, #1f6da8, #3d95cf);
  color: #ffffff;
}

.search-shell {
  display: inline-flex;
  align-items: center;
  gap: 0.7rem;
  min-width: min(360px, 100%);
  padding: 0.85rem 1rem;
  border-radius: 999px;
  background: #f7fbff;
  border: 1px solid rgba(20, 70, 110, 0.1);
  color: #63819d;
}

.search-shell input {
  width: 100%;
  border: none;
  outline: none;
  background: transparent;
  color: #153754;
}

.loading-state,
.empty-state {
  display: grid;
  place-items: center;
  gap: 0.85rem;
  min-height: 320px;
  margin-top: 1.5rem;
  padding: 2rem;
  text-align: center;
  border-radius: 28px;
  background: rgba(255, 255, 255, 0.86);
  border: 1px solid rgba(20, 70, 110, 0.08);
  color: #587086;
  box-shadow: 0 18px 40px rgba(29, 61, 94, 0.06);
}

.empty-state h3 {
  margin: 0;
  color: #163a57;
}

.empty-state p,
.loading-state p {
  margin: 0;
}

.notification-list {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(290px, 1fr));
  gap: 1.15rem;
  margin-top: 1.5rem;
}

.notification-card {
  position: relative;
  overflow: hidden;
  padding: 1.3rem;
  border-radius: 24px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.97), rgba(244, 249, 255, 0.95));
  border: 1px solid rgba(20, 70, 110, 0.08);
  box-shadow: 0 20px 48px rgba(29, 61, 94, 0.07);
}

.notification-card--unread {
  border-color: rgba(17, 103, 165, 0.26);
}

.card-glow {
  position: absolute;
  inset: auto -35px -55px auto;
  width: 110px;
  height: 110px;
  border-radius: 999px;
  background: radial-gradient(circle, rgba(73, 157, 214, 0.24), transparent 70%);
}

.notification-card__top,
.notification-meta,
.notification-actions {
  position: relative;
  z-index: 1;
}

.notification-card__top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}

.notification-index {
  display: inline-flex;
  margin-bottom: 0.6rem;
  padding: 0.26rem 0.65rem;
  border-radius: 999px;
  background: rgba(17, 103, 165, 0.1);
  color: #21679e;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.notification-card h3 {
  margin: 0;
  color: #153754;
  font-size: 1.15rem;
  line-height: 1.25;
}

.timestamp-pill {
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
  padding: 0.45rem 0.7rem;
  border-radius: 999px;
  background: rgba(20, 70, 110, 0.06);
  color: #4d6780;
  font-size: 0.76rem;
  font-weight: 600;
}

.notification-message {
  position: relative;
  z-index: 1;
  margin: 1rem 0 1.15rem;
  color: #587086;
  line-height: 1.7;
}

.notification-meta {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.8rem;
}

.meta-item {
  padding: 0.85rem;
  border-radius: 18px;
  background: rgba(243, 248, 255, 0.9);
  border: 1px solid rgba(20, 70, 110, 0.06);
}

.meta-label {
  display: block;
  margin-bottom: 0.35rem;
  color: #6a8196;
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.meta-item strong {
  color: #173a57;
  font-size: 0.95rem;
}

.notification-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-top: 1rem;
}

.action-button {
  border: none;
  border-radius: 999px;
  padding: 0.7rem 1rem;
  background: linear-gradient(135deg, #1f6da8, #3d95cf);
  color: #fff;
  font-weight: 700;
  box-shadow: 0 12px 24px rgba(31, 109, 168, 0.18);
}

.read-pill {
  display: inline-flex;
  padding: 0.55rem 0.8rem;
  border-radius: 999px;
  background: rgba(31, 109, 168, 0.08);
  color: #205d8b;
  font-size: 0.82rem;
  font-weight: 700;
}

.pagination-shell {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 1.5rem;
  padding: 1rem 1.2rem;
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.85);
  border: 1px solid rgba(20, 70, 110, 0.08);
  box-shadow: 0 16px 36px rgba(29, 61, 94, 0.06);
}

.pagination-copy {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.9rem;
  color: #5d7389;
}

.pagination-copy p,
.pagination-copy span {
  margin: 0;
}

.page-size-shell {
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
}

.page-size-shell select {
  border-radius: 999px;
  padding: 0.45rem 0.8rem;
  border: 1px solid rgba(20, 70, 110, 0.12);
  background: #f7fbff;
  color: #153754;
}

.notifications-page ::v-deep .page-item .page-link {
  border-radius: 999px;
  margin-left: 0.35rem;
  border: none;
  color: #1f6da8;
  background: rgba(31, 109, 168, 0.08);
}

.notifications-page ::v-deep .page-item.active .page-link {
  color: #ffffff;
  background: linear-gradient(135deg, #1f6da8, #3d95cf);
  box-shadow: 0 10px 20px rgba(31, 109, 168, 0.18);
}

@media (max-width: 991px) {
  .notifications-page {
    padding: 0;
  }

  .notifications-shell {
    margin-top: 0;
  }

  .toolbar,
  .pagination-shell {
    grid-template-columns: 1fr;
    flex-direction: column;
    align-items: stretch;
  }

  .notification-modal__hero,
  .notification-modal__actions {
    flex-direction: column;
  }

  .metric-chip {
    width: 100%;
    min-width: 0;
  }

  .notification-meta {
    grid-template-columns: 1fr;
  }

  .search-shell {
    min-width: 0;
    width: 100%;
  }
}

@media (max-width: 767px) {
  .notifications-shell {
    margin-top: 0;
  }
}

@media (max-width: 600px) {
  .notifications-shell {
    margin-top: 0;
  }
}
</style>
